import React, { Component } from 'react';
import { BackHandler, Dimensions, Keyboard, Platform } from 'react-native';

import { withTheme, LightThemeColors } from '@vfgroup-oneplatform/foundation/Components/Themes';
import { ToBiEmojis } from '@vfgroup-oneplatform/foundation/ToBi';
import Navigation from '@vfgroup-oneplatform/foundation/Utils/Navigation';
import { isFunction as _isFunction } from 'lodash';
import memoize from 'memoize-one';
import PropTypes from 'prop-types';

import BlockBox from '../../Components/BlockBox';
import Status from '../../Utils/AuthStatusEnum';
import LoginTestKeys from '../../Utils/LoginTestKeys';
import AuthContainer from '../AuthContainer/AuthContainer';
import withValidation from '../Hoc/withValidation';

import LoginForm from './LoginForm';

class PhoneNumberLogin extends Component {
  static getDerivedStateFromProps(props, state) {
    if (props.isValidPhone === null && (!state.loginDisabled || !state.isValid)) {
      return {
        loginDisabled: true,
        isValid: true,
      };
    } else if (props.isValidPhone === 1 && (state.loginDisabled || !state.isValid)) {
      return {
        loginDisabled: false,
        isValid: true,
      };
    } else if (props.isValidPhone === 0 && (!state.loginDisabled || state.isValid)) {
      return {
        loginDisabled: true,
        isValid: false,
      };
    }

    return null;
  }

  constructor(props) {
    super(props);

    const withFirstLabel = Navigation.instance.getParam(this, 'withFirstLabel', true);
    const withSecondLabel = Navigation.instance.getParam(this, 'withSecondLabel', true);

    this.state = {
      isValid: true,
      loginDisabled: true,
      showErrorBox: false,
      errorMessage: '',
      subErrorMessage: '',
      tobiEmoji: undefined,
      isSendingSms: false,
      withFirstLabel,
      withSecondLabel,
      showBlockDialog: false,
    };

    this.phone = '';

    this.maxLength = Navigation.instance.getParam(this, 'maxLength', () => null);
    this.emailOrPhoneRef = React.createRef();
  }

  onPhoneChanged = text => {
    this.setState({
      showErrorBox: false,
    });
    this.phone = text;

    const { handleValidation } = this.props;
    handleValidation(text);
  };

  onSubmit = async () => {
    const { phone } = this;
    const onNext = Navigation.instance.getParam(this, 'onNext', () => null);
    const mode = Navigation.instance.getParam(this, 'mode', 'softLogin');
    const checkPhoneNumberStatus = Navigation.instance.getParam(
      this,
      'checkPhoneNumberStatus',
      () => null,
    );
    const getSMSCode = Navigation.instance.getParam(this, 'getSMSCode', () => null);
    Keyboard.dismiss();

    this.setState({
      tobiEmoji: ToBiEmojis.Whistle,
      isSendingSms: true,
    });

    if (mode === 'forgotPassword') {
      try {
        await checkPhoneNumberStatus(phone);
      } catch (error) {
        if (error.status === Status.Blocked) {
          BackHandler.addEventListener('hardwareBackPress', this.handleBackButton);
          this.setState({
            showBlockDialog: true,
            tobiEmoji: ToBiEmojis.Surprise,
            isSendingSms: false,
          });
          return;
        }
      }
    }

    try {
      await getSMSCode(phone);
      onNext(phone);

      this.setState({
        tobiEmoji: undefined,
        isSendingSms: false,
      });
    } catch (error) {
      this.setState({
        showErrorBox: true,
        errorMessage: error.errorMessage,
        subErrorMessage: error.subErrorMessage,
        tobiEmoji: ToBiEmojis.Sad,
        isSendingSms: false,
      });
    }
  };

  onEmojiEnd = () => {
    this.setState({
      tobiEmoji: undefined,
    });
  };

  onPressCreateAccount = () => {
    const onRegister = Navigation.instance.getParam(this, 'onRegister', () => null);
    onRegister();
    this.resetToInitialState();
  };

  onPressUseDifferentAccount = () => {
    this.resetToInitialState();
  };

  getErrorProps = (showErrorBox, errorMessage, subErrorMessage) => {
    const errorConfig = Navigation.instance.getParam(this, 'errorConfig', {});
    return {
      showErrorBox,
      errorMessage,
      subErrorMessage,
      accessible: false,
      ...errorConfig,
    };
  };
  getErrorPropsMemoized = memoize(this.getErrorProps);

  resetToInitialState = () => {
    const iseEmailOrPhoneField =
      this.emailOrPhoneRef &&
      this.emailOrPhoneRef.current &&
      _isFunction(this.emailOrPhoneRef.current.setValue);

    iseEmailOrPhoneField && this.emailOrPhoneRef.current.setValue('');

    this.emailOrPhone = '';

    this.setState({
      loginDisabled: true,
      showBlockDialog: false,
      tobiEmoji: undefined,
    });
    Keyboard.dismiss();
    BackHandler.removeEventListener('hardwareBackPress', this.handleBackButton);
  };

  handleBackButton = () => {
    this.closeBlockDialog();
    return true;
  };

  secondLabelOnPress = () => {
    const onUpfrontLogin = Navigation.instance.getParam(this, 'onUpfrontLogin', () => null);
    onUpfrontLogin();
  };

  closeBlockDialog = () => {
    this.setState({
      showBlockDialog: false,
    });
  };

  render() {
    const { theme, navigation } = this.props;
    const {
      isValid,
      showErrorBox,
      errorMessage,
      subErrorMessage,
      tobiEmoji,
      isSendingSms,
      loginDisabled,
      withFirstLabel,
      withSecondLabel,
      showBlockDialog,
    } = this.state;
    const hasUpfrontLogin = Navigation.instance.getParam(this, 'hasUpfrontLogin', () => null);
    const renderTitle = !isSendingSms
      ? Navigation.instance.getParam(this, 'renderTitle', null)
      : () => { };

    const titleStyle = Navigation.instance.getParam(this, 'titleStyle', {});

    return (
      <>
        <AuthContainer
          tobiSize={114}
          tobiEmoji={tobiEmoji}
          onEmojiEnd={this.onEmojiEnd}
          renderTobiLessIconView={Navigation.instance.getParam(this, 'renderLoginIconView', null)}
          errorProps={this.getErrorPropsMemoized(showErrorBox, errorMessage, subErrorMessage)}
          titleStyle={titleStyle}
          renderTitle={renderTitle}
          firstLabelTitle={hasUpfrontLogin ? 'soft_login_footer_first_label' : null}
          firstLabelDisabled
          secondLabelTitle={hasUpfrontLogin ? 'soft_login_footer_second_label' : null}
          secondLabelOnPress={this.secondLabelOnPress}
          tobiTestId={LoginTestKeys.authContainerTobiIcon}
          firstLabelTestID={LoginTestKeys.loginRegisteredTxt}
          secondLabelTestId={LoginTestKeys.loginUsernameLink}
          isLoading={isSendingSms}
          loadingText="verification_verifying_login_code_text"
          withSecondLabel={withSecondLabel}
          withFirstLabel={withFirstLabel}
          childrenContainerAccessibilityLabel={LoginTestKeys.loginAuthContainer}
          {...Platform.select({
            web: {
              backBtn: true,
              backBtnPress: () => navigation?.pop(1)
            },
            default: {

            }
          })
          }
        >
          <LoginForm
            phone={this.phone}
            isValid={isValid}
            showErrorBox={showErrorBox}
            isSendingSms={isSendingSms}
            loginDisabled={loginDisabled}
            emailOrPhoneRef={this.emailOrPhoneRef}
            onPhoneChanged={this.onPhoneChanged}
            maxLength={this.maxLength}
            onSubmit={this.onSubmit}
          />
        </AuthContainer>
        {showBlockDialog && (
          <BlockBox
            onPrimaryBtnPress={this.onPressCreateAccount}
            primaryBtnTitle="reset_password_not_exist_warning_create_account_button"
            onSecondaryBtnPress={this.onPressUseDifferentAccount}
            secondaryBtnTitle="reset_password_not_exist_warning_use_different_account_button"
            text="reset_password_not_exist_warning_text"
            textPlaceholder={this.phone}
            onClose={this.closeBlockDialog}
            theme={theme}
            title="reset_password_not_exist_warning_title"
            subtitle="reset_password_not_exist_warning_subtitle"
          />
        )}
      </>
    );
  }
}

PhoneNumberLogin.defaultProps = {
  theme: LightThemeColors,
};

PhoneNumberLogin.propTypes = {
  theme: PropTypes.object,
  navigation: PropTypes.object.isRequired,
  handleValidation: PropTypes.func,
  isValidPhone: PropTypes.number,
};

export default withValidation(withTheme(PhoneNumberLogin));
